import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { GlassType, Estimate, CompanyInfo } from '@/context/GlassContext';

interface GlassScribeDB extends DBSchema {
  glassTypes: {
    key: string;
    value: GlassType[];
  };
  estimates: {
    key: string;
    value: Estimate[];
  };
  companyInfo: {
    key: string;
    value: CompanyInfo;
  };
}

const DB_CONFIG = {
  name: 'glass-scribe-db',
  version: 1,
  storeNames: ['glassTypes', 'estimates', 'companyInfo'] as const,
  dataRetentionDays: 90
} as const;

let dbInstance: IDBPDatabase<GlassScribeDB> | null = null;

const initDB = async () => {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<GlassScribeDB>(DB_CONFIG.name, DB_CONFIG.version, {
    upgrade(db) {
      DB_CONFIG.storeNames.forEach(storeName => {
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName);
          /* Removed index creation as it's not needed for the current implementation
          if (storeName === 'glassTypes') {
            // Future enhancement: Add indexes if needed
          } */
        }
      });
    },
  });

  return dbInstance;
};

const validateData = <T>(data: T): boolean => {
  if (data === null || data === undefined) return false;
  if (Array.isArray(data) && data.length === 0) return false;
  if (typeof data === 'object' && Object.keys(data).length === 0) return false;
  return true;
};

const cleanupOldData = async (db: IDBPDatabase<GlassScribeDB>) => {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - DB_CONFIG.dataRetentionDays);

  const tx = db.transaction('estimates', 'readwrite');
  const store = tx.objectStore('estimates');
  
  const estimates = await store.get('data') as Estimate[] | undefined;
  if (!estimates) return;

  const filteredEstimates = estimates.filter(
    estimate => new Date(estimate.created) >= cutoffDate
  );

  if (filteredEstimates.length < estimates.length) {
    await store.put(filteredEstimates, 'data');
  }

  await tx.done;
};

type StoreKey = 'glassTypes' | 'estimates' | 'companyInfo';
type StoreValue<T extends StoreKey> = T extends 'glassTypes' ? GlassType[] : T extends 'estimates' ? Estimate[] : CompanyInfo;

class IndexedDBError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = 'IndexedDBError';
  }
}

export const indexedDBService = {
  async getData<T extends StoreKey>(key: T, defaultValue: StoreValue<T>): Promise<StoreValue<T>> {
    try {
      const db = await initDB();
      const tx = db.transaction(key, 'readonly');
      const store = tx.objectStore(key);
      const data = await store.get('data');
      await tx.done;

      if (!data) return defaultValue;
      if (!validateData(data)) return defaultValue;
      
      return data as StoreValue<T>;
    } catch (error) {
      throw new IndexedDBError(`Failed to load ${key} from IndexedDB`, error);
    }
  },

  async setData<T extends StoreKey>(key: T, data: StoreValue<T>): Promise<void> {
    if (!validateData(data)) {
      throw new IndexedDBError(`Invalid data for ${key}`);
    }

    try {
      const db = await initDB();
      const tx = db.transaction(key, 'readwrite');
      const store = tx.objectStore(key);
      await store.put(data, 'data');
      await tx.done;

      // Cleanup old data periodically when saving estimates
      if (key === 'estimates') {
        await cleanupOldData(db);
      }
    } catch (error) {
      throw new IndexedDBError(`Failed to save ${key} to IndexedDB`, error);
    }
  },

  async savePDF(pdfData: string, filename: string): Promise<string | null> {
    try {
      // Validate PDF data
      if (!pdfData.startsWith('data:application/pdf')) {
        throw new Error('Invalid PDF data format');
      }

      // Create and click download link in a more controlled way
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = pdfData;
      link.download = filename;

      // Add to document, click, and cleanup
      document.body.appendChild(link);
      link.click();
      
      // Use setTimeout to ensure the download starts before cleanup
      await new Promise(resolve => setTimeout(resolve, 100));
      document.body.removeChild(link);
      
      return filename;
    } catch (error) {
      throw new IndexedDBError('Failed to save PDF', error);
    }
  }
};